import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Scene } from '@babylonjs/core/scene';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';

type WeaponConfig = {
  name: string;
  damage: number;
  fireRate: number;
  recoilPitch: number;
  recoilYaw: number;
  spread: number;
  isAutomatic: boolean;
  muzzleOffset: Vector3;
  range: number;
};

class WeaponController {
  game: any;
  currentWeapon: WeaponConfig | null;
  _weapons: WeaponConfig[];
  _weaponIndex: number;
  _fireCooldown: number;
  _isFiring: boolean;
  _muzzleFlashMesh: any;
  _muzzleFlashMat: any;
  _muzzleFlashTimer: number;
  _bulletImpactDuration: number;
  _impacts: { mesh: any; timer: number }[];
  _weaponModel: any;

  constructor(game: any) {
    this.game = game;
    this.currentWeapon = null;
    this._weapons = [];
    this._weaponIndex = 0;
    this._fireCooldown = 0;
    this._isFiring = false;
    this._muzzleFlashMesh = null;
    this._muzzleFlashMat = null;
    this._muzzleFlashTimer = 0;
    this._bulletImpactDuration = 0.5;
    this._impacts = [];
    this._weaponModel = null;

    this._weapons = [
      {
        name: 'Pistol',
        damage: 25,
        fireRate: 0.3,
        recoilPitch: 0.02,
        recoilYaw: 0.008,
        spread: 0.01,
        isAutomatic: false,
        muzzleOffset: new Vector3(0.3, -0.2, -0.8),
        range: 100,
      },
      {
        name: 'Rifle',
        damage: 18,
        fireRate: 0.1,
        recoilPitch: 0.012,
        recoilYaw: 0.004,
        spread: 0.02,
        isAutomatic: true,
        muzzleOffset: new Vector3(0.3, -0.2, -0.8),
        range: 120,
      },
      {
        name: 'Shotgun',
        damage: 12,
        fireRate: 0.8,
        recoilPitch: 0.05,
        recoilYaw: 0.02,
        spread: 0.08,
        isAutomatic: false,
        muzzleOffset: new Vector3(0.3, -0.2, -0.8),
        range: 30,
      },
    ];

    this.currentWeapon = this._weapons[0];
  }

  init(scene: Scene, characterMesh: any) {
    this._createWeaponModel(scene, characterMesh);
    this._createMuzzleFlash(scene);
  }

  _createWeaponModel(scene: Scene, characterMesh: any) {
    const barrel = MeshBuilder.CreateCylinder('weaponBarrel', {
      height: 0.6,
      diameterTop: 0.04,
      diameterBottom: 0.06,
      tessellation: 8,
    }, scene);
    const barrelMat = new StandardMaterial('weaponBarrelMat', scene);
    barrelMat.diffuseColor = new Color3(0.15, 0.15, 0.18);
    barrelMat.specularColor = new Color3(0.3, 0.3, 0.3);
    barrel.material = barrelMat;
    barrel.rotation.x = Math.PI / 2;
    barrel.position = new Vector3(0.3, -0.15, -0.5);
    barrel.parent = characterMesh;

    const body = MeshBuilder.CreateBox('weaponBody', {
      width: 0.06,
      height: 0.08,
      depth: 0.25,
    }, scene);
    const bodyMat = new StandardMaterial('weaponBodyMat', scene);
    bodyMat.diffuseColor = new Color3(0.2, 0.2, 0.22);
    bodyMat.specularColor = new Color3(0.2, 0.2, 0.2);
    body.material = bodyMat;
    body.position = new Vector3(0.3, -0.2, -0.35);
    body.parent = characterMesh;

    this._weaponModel = barrel;
  }

  _createMuzzleFlash(scene: Scene) {
    const flash = MeshBuilder.CreatePlane('muzzleFlash', { size: 0.15 }, scene);
    const flashMat = new StandardMaterial('muzzleFlashMat', scene);
    flashMat.emissiveColor = new Color3(1, 0.8, 0.3);
    flashMat.diffuseColor = new Color3(1, 0.8, 0.3);
    flashMat.alpha = 0;
    flashMat.backFaceCulling = false;
    flashMat.disableLighting = true;
    flash.material = flashMat;
    flash.billboardMode = 7;

    this._muzzleFlashMesh = flash;
    this._muzzleFlashMat = flashMat;
  }

  switchWeapon(index: number) {
    if (index < 0 || index >= this._weapons.length) return;
    this._weaponIndex = index;
    this.currentWeapon = this._weapons[index];
  }

  nextWeapon() {
    this.switchWeapon((this._weaponIndex + 1) % this._weapons.length);
  }

  startFiring() {
    this._isFiring = true;
    this._tryFire();
  }

  stopFiring() {
    this._isFiring = false;
  }

  _tryFire() {
    if (!this.currentWeapon || this._fireCooldown > 0) return;

    this._fire();
    this._fireCooldown = this.currentWeapon.fireRate;

    if (this.currentWeapon.isAutomatic && this._isFiring) {
      setTimeout(() => this._tryFire(), this.currentWeapon.fireRate * 1000);
    }
  }

  _fire() {
    if (!this.currentWeapon || !this.game.thirdPersonCameraController) return;

    const camera = this.game.thirdPersonCameraController;
    const aimRay = camera.getAimRay();

    const spreadX = (Math.random() - 0.5) * this.currentWeapon.spread;
    const spreadY = (Math.random() - 0.5) * this.currentWeapon.spread;
    const direction = aimRay.direction.add(new Vector3(spreadX, spreadY, 0)).normalize();

    const scene = this.game.scene;
    if (!scene) return;

    const ray = new Ray(aimRay.origin, direction, this.currentWeapon.range);
    const hit = scene.pickWithRay(ray, (mesh: any) => {
      return mesh.isPickable && !mesh.name.startsWith('weapon') && !mesh.name.startsWith('muzzle') && !mesh.name.startsWith('CharacterImpostor');
    });

    this._showMuzzleFlash();
    this._showBulletTrail(aimRay.origin, hit && hit.hit ? hit.pickedPoint : ray.origin.add(direction.scale(this.currentWeapon.range)));

    if (hit && hit.hit) {
      this._showBulletImpact(hit.pickedPoint, hit.getNormal(true) || Vector3.Up());

      if (this.game.crosshairController) {
        this.game.crosshairController.showHitMarker();
      }

      if (hit.pickedMesh && hit.pickedMesh.metadata && hit.pickedMesh.metadata.isPlayer) {
        const playerData = hit.pickedMesh.metadata;
        if (this.game.playerCombatController) {
          this.game.playerCombatController.applyDamageToPlayer(
            playerData.playerId,
            this.currentWeapon.damage,
            this.game.playerSlot || 'LocalPlayer'
          );
        }
      }

      if (hit.pickedMesh && hit.pickedMesh.metadata && hit.pickedMesh.isTarget) {
        if (hit.pickedMesh.metadata.controller && hit.pickedMesh.metadata.controller.hitTarget) {
          hit.pickedMesh.metadata.controller.hitTarget(hit.pickedMesh);
        }
      }
    }

    camera.applyRecoil(this.currentWeapon.recoilPitch, (Math.random() - 0.5) * this.currentWeapon.recoilYaw * 2);

    if (this.game.multiplayerConnectionController && this.game.multiplayerConnectionController.socket) {
      const ws = this.game.multiplayerConnectionController.socket;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'shoot',
          weapon: this.currentWeapon.name,
          origin: { x: aimRay.origin.x, y: aimRay.origin.y, z: aimRay.origin.z },
          direction: { x: direction.x, y: direction.y, z: direction.z },
        }));
      }
    }
  }

  _showMuzzleFlash() {
    if (!this._muzzleFlashMat || !this._muzzleFlashMesh) return;
    this._muzzleFlashMat.alpha = 1;
    this._muzzleFlashTimer = 0.04;

    if (this._muzzleFlashMesh.parent !== this._weaponModel) {
      this._muzzleFlashMesh.parent = this._weaponModel;
    }
    this._muzzleFlashMesh.position = this.currentWeapon ? this.currentWeapon.muzzleOffset : Vector3.Zero();
  }

  _showBulletTrail(from: Vector3, to: Vector3) {
    const scene = this.game.scene;
    if (!scene) return;

    const distance = Vector3.Distance(from, to);
    if (distance < 0.1) return;

    const direction = to.subtract(from).normalize();
    const center = from.add(direction.scale(distance / 2));

    const trail = MeshBuilder.CreateTube('bulletTrail', {
      path: [from, to],
      radius: 0.005,
      tessellation: 4,
      updatable: false,
    }, scene);

    const trailMat = new StandardMaterial('bulletTrailMat_' + Date.now(), scene);
    trailMat.emissiveColor = new Color3(1, 0.9, 0.5);
    trailMat.alpha = 0.6;
    trailMat.disableLighting = true;
    trail.material = trailMat;

    setTimeout(() => {
      trail.dispose();
      trailMat.dispose();
    }, 50);
  }

  _showBulletImpact(position: Vector3, normal: Vector3) {
    const scene = this.game.scene;
    if (!scene) return;

    const impact = MeshBuilder.CreateDisc('bulletImpact_' + Date.now(), {
      radius: 0.05,
      tessellation: 8,
    }, scene);
    impact.position = position.add(normal.scale(0.01));

    const lookTarget = position.add(normal);
    impact.lookAt(lookTarget);

    const impactMat = new StandardMaterial('bulletImpactMat_' + Date.now(), scene);
    impactMat.emissiveColor = new Color3(1, 0.8, 0.3);
    impactMat.alpha = 0.8;
    impactMat.disableLighting = true;
    impact.material = impactMat;

    this._impacts.push({ mesh: impact, timer: this._bulletImpactDuration });
  }

  update(delta: number) {
    if (this._fireCooldown > 0) {
      this._fireCooldown -= delta;
    }

    if (this._muzzleFlashTimer > 0) {
      this._muzzleFlashTimer -= delta;
      if (this._muzzleFlashTimer <= 0 && this._muzzleFlashMat) {
        this._muzzleFlashMat.alpha = 0;
      }
    }

    for (let i = this._impacts.length - 1; i >= 0; i--) {
      this._impacts[i].timer -= delta;
      if (this._impacts[i].timer <= 0) {
        this._impacts[i].mesh.dispose();
        this._impacts.splice(i, 1);
      }
    }
  }

  dispose() {
    if (this._muzzleFlashMesh) {
      this._muzzleFlashMesh.dispose();
    }
    if (this._muzzleFlashMat) {
      this._muzzleFlashMat.dispose();
    }
    if (this._weaponModel) {
      this._weaponModel.dispose();
    }
    for (const impact of this._impacts) {
      impact.mesh.dispose();
    }
    this._impacts = [];
  }
}

export { WeaponController };
export type { WeaponConfig };
